// src/components/Navigation.js
import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap'; // GSAPは引き続き使用
import './Navigation.css'; // 外部CSSファイルを使用

// ナビゲーションメニューのデータ
// Missionは mission セクションに移動する
const menuItems = [
  { id: 'mission', label: 'Mission' },
  { id: 'service', label: 'Service' },
  { id: 'store', label: 'Store' },
  { id: 'company', label: 'Company' },
];

const Navigation = ({ position = 'right' }) => {
  const [activeId, setActiveId] = useState('mission'); // アクティブID（ナビの選択状態）
  const [visualSectionId, setVisualSectionId] = useState('hero'); // 見た目（配色）用のセクションID
  const listRef = useRef(null); // ul要素の参照
  const dotRef = useRef(null); // ピンクの点（インジケーター）の参照
  const barTopRef = useRef(null); // 上のバーの参照
  const barBottomRef = useRef(null); // 下のバーの参照
  const itemRefs = useRef({}); // 各li要素の参照をIDで管理

  // Intersection Observer によるスクロール時のアクティブセクション判定（ナビ選択用）
  useEffect(() => {
    const observerOptions = {
      root: null, // ビューポートをルート
      rootMargin: '0px',
      threshold: 0.5, // 要素が50%ビューポートに入ったら
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Intersection Observerでアクティブが切り替わったら、そのIDをセット
          setActiveId(entry.target.id);
        }
      });
    }, observerOptions);

    // menuItemsを使用してセクションを監視
    menuItems.forEach(item => {
      const section = document.getElementById(item.id);
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      observer.disconnect(); // クリーンアップ
    };
  }, []);

  // 配色切り替え用の広範囲オブザーバー（hero, mission, service, store, company）
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };

    const themeObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisualSectionId(entry.target.id);
        }
      });
    }, observerOptions);

    const themeSectionIds = ['hero', 'mission', 'service', 'store', 'company'];
    themeSectionIds.forEach((id) => {
      const section = document.getElementById(id);
      if (section) themeObserver.observe(section);
    });

    return () => themeObserver.disconnect();
  }, []);

  // インジケーターの状態を更新する中央集権的な関数 (GSAPを使用)
  const updateIndicators = (targetId, isHover = false) => {
    const targetItem = itemRefs.current[targetId];
    if (!targetItem || !listRef.current || !dotRef.current || !barTopRef.current || !barBottomRef.current) return;

    const navItemsElements = Array.from(listRef.current.children).filter(
      (child) => child.tagName === 'LI'
    );
    const pinkDot = dotRef.current;
    const barTopEl = barTopRef.current;
    const barBottomEl = barBottomRef.current;
    
    // ピンクの円をターゲット要素へ移動
    const dotTop = targetItem.offsetTop + targetItem.offsetHeight / 2 - pinkDot.offsetHeight / 2;
    gsap.to(pinkDot, {
      top: dotTop,
      opacity: 1,
      duration: isHover ? 0.2 : 0.4, // ホバー時は速く、スクロール/クリック時はゆっくり
      ease: isHover ? 'power2.out' : 'cubic-bezier(0.45, 0, 0.2, 1)',
    });

    // 上部バー: 最初のアイテムからターゲットアイテムの上端まで
    gsap.to(barTopEl, {
        top: navItemsElements[0] ? navItemsElements[0].offsetTop : 0, // 最初のliのtopから開始
        height: targetItem.offsetTop - (navItemsElements[0] ? navItemsElements[0].offsetTop : 0),
        opacity: 1,
        duration: isHover ? 0.2 : 0.4,
        ease: isHover ? 'power2.out' : 'cubic-bezier(0.45, 0, 0.2, 1)',
    });

    // 下部バー: ターゲットアイテムの下端から最後のアイテムまで
    gsap.to(barBottomEl, {
        top: targetItem.offsetTop + targetItem.offsetHeight, // ターゲットアイテムの下端から開始
        height: (navItemsElements[navItemsElements.length - 1].offsetTop + navItemsElements[navItemsElements.length - 1].offsetHeight) - (targetItem.offsetTop + targetItem.offsetHeight),
        opacity: 1,
        duration: isHover ? 0.2 : 0.4,
        ease: isHover ? 'power2.out' : 'cubic-bezier(0.45, 0, 0.2, 1)',
    });
  };

  // activeIdの変更または初期ロード時にインジケーターを更新
  useEffect(() => {
    // DOMが完全に準備できてから実行するためにsetTimeoutでわずかに遅延
    const timer = setTimeout(() => {
      const initialActiveItem = itemRefs.current[activeId];
      if (initialActiveItem) {
        updateIndicators(activeId); // IDを渡す
      }
    }, 100); 
    return () => clearTimeout(timer);
  }, [activeId]); // activeIdが変更されたら再実行

  // マウスイベントハンドラー
  const handleMouseEnter = (id) => {
    updateIndicators(id, true); // ホバー時
  };

  const handleMouseLeave = () => {
    // マウスがリストから離れたら、アクティブな項目に戻す
    updateIndicators(activeId, false); // ホバー解除時
  };

  const handleClick = (id) => {
    setActiveId(id); // クリックでアクティブIDを変更
    // スムーズスクロール
    const targetElement = document.getElementById(id);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav 
      className={`navigation ${position} section-${visualSectionId}`} 
      onMouseLeave={handleMouseLeave}
    > 
      <ul ref={listRef} className="nav-list">
        {menuItems.map((item) => (
          <li
            key={item.id}
            ref={(el) => (itemRefs.current[item.id] = el)}
            className={activeId === item.id ? 'active' : ''}
            onMouseEnter={() => handleMouseEnter(item.id)}
            onClick={() => handleClick(item.id)}
          >
            <a href={`#${item.id}`}>
              {item.label}
            </a>
          </li>
        ))}

        {/* ピンクの点とバーのコンテナ */}
        <div className="indicator-container">
            <div ref={dotRef} className="pink-dot"></div>
            <div ref={barTopRef} className="connected-bar bar-top"></div>
            <div ref={barBottomRef} className="connected-bar bar-bottom"></div>
        </div>
      </ul>
    </nav>
  );
};

export default Navigation;